import axios from 'axios';
import { API_LOCATION } from '../api-config';

/**
 * Service for managing promo code rules
 */
class PromoRulesService {
  /**
   * Get authentication token from localStorage
   */
  static async getAuthToken() {
    // This matches the pattern used in PromoCodes.vue
    const { Auth } = await import('@aws-amplify/auth');
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  }

  /**
   * Get company and group IDs from localStorage
   */
  static getCompanyInfo() {
    const companyId = localStorage.getItem('companyId');
    const group = JSON.parse(localStorage.getItem('group'));
    const companyGroupId = group ? group.id : null;

    if (!companyId || !companyGroupId) {
      throw new Error('Company or group information not found');
    }

    return { companyId, companyGroupId };
  }

  /**
   * Fetch available rule types
   * @returns {Promise<Array>} Array of rule type objects
   */
  static async getAvailableRuleTypes() {
    try {
      const token = await this.getAuthToken();
      const { companyId, companyGroupId } = this.getCompanyInfo();

      const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/promo-rule-types`;
      const config = { headers: { Authorization: token } };

      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      console.error('Error fetching rule types:', error);
      throw error;
    }
  }

  /**
   * Fetch rules for a specific promo code
   * @param {number} promoId - The promo code ID
   * @returns {Promise<Array>} Array of rule objects
   */
  static async getPromoRules(promoId) {
    try {
      const token = await this.getAuthToken();
      const { companyId, companyGroupId } = this.getCompanyInfo();

      const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/promos/${promoId}/rules`;
      const config = { headers: { Authorization: token } };

      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      console.error('Error fetching promo rules:', error);
      // Return empty array if rules don't exist yet
      if (error.response && error.response.status === 404) {
        return [];
      }
      throw error;
    }
  }

  /**
   * Create a new rule for a promo code
   * @param {number} promoId - The promo code ID
   * @param {Object} ruleData - The rule configuration
   * @returns {Promise<Object>} Created rule object
   */
  static async createPromoRule(promoId, ruleData) {
    try {
      const token = await this.getAuthToken();
      const { companyId, companyGroupId } = this.getCompanyInfo();

      const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/promos/${promoId}/rules`;
      const config = { headers: { Authorization: token } };

      const response = await axios.post(url, ruleData, config);
      return response.data;
    } catch (error) {
      console.error('Error creating promo rule:', error);
      throw error;
    }
  }

  /**
   * Update an existing rule
   * @param {number} promoId - The promo code ID
   * @param {number} ruleId - The rule ID
   * @param {Object} ruleData - The updated rule configuration
   * @returns {Promise<Object>} Updated rule object
   */
  static async updatePromoRule(promoId, ruleId, ruleData) {
    try {
      const token = await this.getAuthToken();
      const { companyId, companyGroupId } = this.getCompanyInfo();

      const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/promos/${promoId}/rules/${ruleId}`;
      const config = { headers: { Authorization: token } };

      const response = await axios.put(url, ruleData, config);
      return response.data;
    } catch (error) {
      console.error('Error updating promo rule:', error);
      throw error;
    }
  }

  /**
   * Delete a rule
   * @param {number} promoId - The promo code ID
   * @param {number} ruleId - The rule ID
   * @returns {Promise<void>}
   */
  static async deletePromoRule(promoId, ruleId) {
    try {
      const token = await this.getAuthToken();
      const { companyId, companyGroupId } = this.getCompanyInfo();

      const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/promos/${promoId}/rules/${ruleId}`;
      const config = { headers: { Authorization: token } };

      await axios.delete(url, config);
    } catch (error) {
      console.error('Error deleting promo rule:', error);
      throw error;
    }
  }

  /**
   * Save all rules for a promo code (batch create)
   * Deletes existing rules and creates new ones
   * @param {number} promoId - The promo code ID
   * @param {Array} rules - Array of rule objects
   * @returns {Promise<void>}
   */
  static async saveAllPromoRules(promoId, rules) {
    try {
      const token = await this.getAuthToken();
      const { companyId, companyGroupId } = this.getCompanyInfo();

      // First, get existing rules
      const existingRules = await this.getPromoRules(promoId);

      // Delete all existing rules
      for (const rule of existingRules) {
        if (rule.id) {
          await this.deletePromoRule(promoId, rule.id);
        }
      }

      // Create new rules
      for (const rule of rules) {
        await this.createPromoRule(promoId, rule);
      }
    } catch (error) {
      console.error('Error saving all promo rules:', error);
      throw error;
    }
  }

  /**
   * Validate rule configuration
   * @param {string} ruleKey - The rule type key (e.g., 'MIN_QUANTITY')
   * @param {Object} config - The rule configuration
   * @returns {Object} Validation result with { valid: boolean, errors: Array }
   */
  static validateRuleConfig(ruleKey, config) {
    const errors = [];

    switch (ruleKey) {
      case 'MIN_QUANTITY': {
        const hasMin = config.min_quantity != null;
        const hasMax = config.max_quantity != null;
        if (!hasMin && !hasMax) {
          errors.push('Set a minimum, a maximum, or both');
        }
        if (hasMin && config.min_quantity < 1) {
          errors.push('Minimum quantity must be at least 1');
        }
        if (hasMax && config.max_quantity < 1) {
          errors.push('Maximum quantity must be at least 1');
        }
        if (hasMin && hasMax && config.max_quantity < config.min_quantity) {
          errors.push('Maximum quantity must be greater than or equal to the minimum quantity');
        }
        if (!config.applies_to) {
          errors.push('Please select what to count');
        }
        break;
      }

      case 'BUY_X_GET_Y':
        // Required items validation
        if (!config.required_items || !config.required_items.item_type) {
          errors.push('Required items type must be specified');
        }
        if (config.required_items && config.required_items.min_quantity < 1) {
          errors.push('Required items minimum quantity must be at least 1');
        }

        // Discounted items validation
        if (!config.discounted_items || !config.discounted_items.item_type) {
          errors.push('Discounted items type must be specified');
        }
        if (config.discounted_items && (!config.discounted_items.item_ids || config.discounted_items.item_ids.length === 0)) {
          errors.push('Please select at least one discounted item');
        }
        if (config.discounted_items && !config.discounted_items.discount_type) {
          errors.push('Discount type must be specified');
        }
        if (config.discounted_items && config.discounted_items.discount_type !== 'free') {
          if (!config.discounted_items.discount_value || config.discounted_items.discount_value <= 0) {
            errors.push('Discount value must be greater than 0');
          }
        }
        break;

      default:
        errors.push('Unknown rule type');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Evaluate whether the current cart satisfies a promo's eligibility rules.
   * Mirrors backend enforcement (apply-promo-code) for the MIN_QUANTITY rule,
   * which supports a minimum, a maximum, or a range. BUY_X_GET_Y is enforced
   * server-side, so it always passes here.
   *
   * @param {Object} promoCode - promo with a `rules` array
   * @param {Object} cart - { participants, lineItems, uniqueItems }
   * @returns {{ valid: boolean, error_message?: string }}
   */
  static evaluateCartEligibility(promoCode, cart) {
    if (!promoCode || !promoCode.rules || promoCode.rules.length === 0) {
      return { valid: true };
    }

    // All rules must pass (AND logic), matching the backend
    for (const rule of promoCode.rules) {
      const ruleKey = (rule.rule_type && rule.rule_type.rule_key) || rule.rule_key;
      if (ruleKey === 'MIN_QUANTITY') {
        // rule_config may arrive parsed or as a JSON string depending on the endpoint
        const config = typeof rule.rule_config === 'string'
          ? JSON.parse(rule.rule_config)
          : rule.rule_config;
        const result = this.evaluateQuantityRule(config, cart);
        if (!result.valid) {
          return result;
        }
      }
      // BUY_X_GET_Y is validated server-side
    }

    return { valid: true };
  }

  /**
   * Check a single quantity rule against the cart. Enforces an optional
   * min_quantity floor and/or max_quantity ceiling; an absent bound is not
   * enforced (mirrors the backend `!= null` guards).
   *
   * @param {Object} config - { min_quantity, max_quantity, applies_to, error_message }
   * @param {Object} cart - { participants, lineItems, uniqueItems }
   * @returns {{ valid: boolean, error_message?: string }}
   */
  static evaluateQuantityRule(config, cart) {
    if (!config) return { valid: true };

    let quantity;
    switch (config.applies_to) {
      case 'total_participants':
        quantity = cart.participants || 0;
        break;
      case 'line_items':
        quantity = cart.lineItems || 0;
        break;
      case 'unique_items':
        quantity = cart.uniqueItems || 0;
        break;
      default:
        return { valid: true };
    }

    if (config.min_quantity != null && quantity < config.min_quantity) {
      return {
        valid: false,
        error_message: config.error_message ||
          `Minimum quantity of ${config.min_quantity} required (current: ${quantity})`
      };
    }

    if (config.max_quantity != null && quantity > config.max_quantity) {
      return {
        valid: false,
        error_message: config.error_message ||
          `Maximum quantity of ${config.max_quantity} allowed (current: ${quantity})`
      };
    }

    return { valid: true };
  }
}

export default PromoRulesService;
